import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ResourceMenu, hasAnyResource } from "@/features/resources";
import { useReadOnly } from "@/lib/read-only";
import { cn } from "@/lib/utils/cn";

import { SegmentedAnswer } from "./SegmentedAnswer";
import { Textarea } from "./controls";
import { useResourceDirectoryFlags } from "../api";
import { ANSWER_VALUES, type Action, type AnswerType } from "../types";

export interface ActionAnswerDraft {
  answer: AnswerType | null;
  comment: string;
  showCommentPublicly: boolean;
  /** Optional 1–5 importance rating; orients the action-plan priority score. */
  priority: number | null;
}

const PRIORITY_VALUES = [1, 2, 3, 4, 5] as const;

/** State-coloured left edge — mirrors the semantic answer colours. */
const STATE_BAR: Record<AnswerType, string> = {
  [ANSWER_VALUES.CarriedOut]: "bg-success",
  [ANSWER_VALUES.CourseOfAction]: "bg-theme-economic",
  [ANSWER_VALUES.NotApplicable]: "bg-border",
};

interface ActionAnswerRowProps {
  action: Action;
  draft: ActionAnswerDraft;
  onChange: (next: ActionAnswerDraft) => void;
  /**
   * "card" (default): boxed, with the action name as a heading + a state-coloured
   * edge — used in the browse list. "bare": no box/heading — used in Focus mode,
   * where the name is already the card heading.
   */
  variant?: "card" | "bare";
}

/**
 * A single action (question): the three-way answer selector plus optional
 * importance and a progress comment. The comment starts collapsed behind an
 * "add a note" toggle (revealed automatically when a note already exists), so a
 * long goal isn't a wall of empty textareas. Purely controlled — the parent
 * owns the draft and triggers autosave on change.
 */
export function ActionAnswerRow({
  action,
  draft,
  onChange,
  variant = "card",
}: ActionAnswerRowProps) {
  const { t } = useTranslation();
  const bare = variant === "bare";
  // A view-only partner (no edit consent) can read the answers but not change
  // them (G-11); SegmentedAnswer disables itself via the same context.
  const readOnly = useReadOnly();
  const directories = useResourceDirectoryFlags();

  // Comment applies to answered actions; importance only to "Piste d'action"
  // (course_of_action) — parity with the old questionnaire, which reveals the
  // 1–5 control only for that answer (C-2). Another answer hides the rating
  // but keeps it: the action plan reads it, and marking an action done turns
  // its answer into "Réalisée".
  const showComment =
    draft.answer === ANSWER_VALUES.CarriedOut ||
    draft.answer === ANSWER_VALUES.CourseOfAction;
  const showImportance = draft.answer === ANSWER_VALUES.CourseOfAction;

  // The note is visible when it already holds text (seeded/edited) or the user
  // opened it; otherwise a quiet toggle stands in its place.
  const hasComment = draft.comment.trim().length > 0;
  const [expanded, setExpanded] = useState(false);
  const noteVisible = showComment && (expanded || hasComment);
  // No "add a note" affordance in read-only consult mode (an existing note still
  // shows, read-only).
  const showToggle = showComment && !noteVisible && !readOnly;
  const resourcesAvailable = hasAnyResource(directories, action);

  const barClass = draft.answer ? STATE_BAR[draft.answer] : "bg-transparent";

  const body = (
    <>
      {!bare && (
        <p className="text-sm font-semibold leading-snug">{action.name}</p>
      )}

      <SegmentedAnswer
        value={draft.answer}
        ariaLabel={action.name}
        onChange={(value) => onChange({ ...draft, answer: value })}
      />

      {showImportance && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("questionnaire.answers.importanceLabel")}
          </span>
          <div
            role="radiogroup"
            aria-label={t("questionnaire.answers.importanceLabel")}
            className="flex gap-1"
          >
            {PRIORITY_VALUES.map((value) => {
              const active = draft.priority === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={String(value)}
                  disabled={readOnly}
                  className={cn(
                    "size-8 rounded-md border text-sm transition-colors",
                    active
                      ? "border-primary bg-primary font-medium text-primary-foreground"
                      : "border-input hover:bg-accent hover:text-accent-foreground",
                    readOnly && "cursor-not-allowed opacity-70",
                  )}
                  onClick={() =>
                    onChange({ ...draft, priority: active ? null : value })
                  }
                >
                  {value}
                </button>
              );
            })}
          </div>
          <span className="text-xs text-muted-foreground">
            {t("questionnaire.answers.importanceHint")}
          </span>
        </div>
      )}

      {noteVisible && (
        <div className="space-y-1.5">
          <Textarea
            autoFocus={expanded}
            readOnly={readOnly}
            aria-label={t("questionnaire.answers.commentLabel")}
            placeholder={t("questionnaire.answers.commentPlaceholder")}
            value={draft.comment}
            onChange={(e) => onChange({ ...draft, comment: e.target.value })}
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              disabled={readOnly}
              checked={draft.showCommentPublicly}
              onChange={(e) =>
                onChange({ ...draft, showCommentPublicly: e.target.checked })
              }
            />
            {t("questionnaire.answers.showCommentPublicly")}
          </label>
        </div>
      )}

      {/* The card's closing line: adding a note on the left, resources on the
          right. Resources show whatever the answer, not only once "Piste
          d'action" is picked — knowing an expert or a subvention exists is
          precisely what should inform that choice. Both are gated, so the line
          disappears rather than leaving an empty gap in the stack. */}
      {(showToggle || resourcesAvailable) && (
        <div className="flex flex-wrap items-center gap-3">
          {showToggle && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
            >
              <Plus className="size-4" aria-hidden />
              {t("questionnaire.answers.addNote")}
            </button>
          )}
          {resourcesAvailable && (
            <div className="ml-auto">
              <ResourceMenu
                actionId={action.id}
                actionName={action.name}
                directories={directories}
                availability={action}
                variant="full"
                favoritesDefault={false}
              />
            </div>
          )}
        </div>
      )}
    </>
  );

  if (bare) return <div className="space-y-3">{body}</div>;

  return (
    <div className="relative space-y-3 overflow-hidden rounded-xl border border-border bg-card p-4 pl-5">
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1", barClass)} />
      {body}
    </div>
  );
}
